import React, { useState, useEffect, useMemo } from 'react';
import { Search, X } from 'lucide-react';
import { getAllSymbols, MathSymbol, refreshSymbolCache } from '../utils/mathSymbols';
import { recordSymbolUsage, getSymbolUsages, symbolKey } from '../utils/symbolUsage';

interface MathSymbolPickerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onInsert: (symbol: MathSymbol) => void;
}

export const MathSymbolPickerModal: React.FC<MathSymbolPickerModalProps> = ({
    isOpen,
    onClose,
    onInsert
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [symbols, setSymbols] = useState<MathSymbol[]>([]);
    const [activeCategory, setActiveCategory] = useState<string>('All');

    useEffect(() => {
        if (isOpen) {
            refreshSymbolCache();
            setSymbols(getAllSymbols());
            setSearchQuery('');
            setActiveCategory('All');
        }
    }, [isOpen]);

    const filteredSymbols = useMemo(() => {
        let filtered = symbols;

        if (searchQuery) {
            const lowerQuery = searchQuery.toLowerCase();
            filtered = filtered.filter(s =>
                s.description.toLowerCase().includes(lowerQuery) ||
                s.trigger.some(t => t.toLowerCase().includes(lowerQuery)) ||
                s.latex.toLowerCase().includes(lowerQuery) ||
                (s.category && s.category.toLowerCase().includes(lowerQuery))
            );
            const usages = getSymbolUsages();
            if (Object.keys(usages).length > 0) {
                filtered = [...filtered].sort((a, b) => (usages[symbolKey(b)] || 0) - (usages[symbolKey(a)] || 0));
            }
        } else if (activeCategory !== 'All') {
            filtered = filtered.filter(s => s.category === activeCategory);
        }

        return filtered;
    }, [symbols, searchQuery, activeCategory]);

    const categories = useMemo(() => {
        const cats = new Set(symbols.map(s => s.category || 'Other'));
        return ['All', ...Array.from(cats).sort()];
    }, [symbols]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200" onClick={onClose}>
            <div
                className="bg-white dark:bg-slate-900 w-full max-w-2xl max-h-[80vh] rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 flex flex-col overflow-hidden animate-in zoom-in-95 duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-4 border-b dark:border-slate-800 bg-gray-50 dark:bg-slate-950 flex items-center justify-between shrink-0">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <span className="text-xl">∑</span> Insert Math Symbol
                    </h3>
                    <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-800 rounded-full transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Search Bar */}
                <div className="p-4 border-b dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0 space-y-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            autoFocus
                            placeholder="Search by name, trigger (e.g., 'lambda'), or LaTeX..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-gray-100 dark:bg-slate-800 border-none rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-gray-900 dark:text-white"
                        />
                    </div>

                    {/* Category Tabs (Horizontal Scroll) */}
                    {!searchQuery && (
                        <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar mask-fade-right">
                            {categories.map(cat => (
                                <button
                                    key={cat}
                                    onClick={() => setActiveCategory(cat)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors border ${activeCategory === cat
                                        ? 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800'
                                        : 'bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700'
                                        }`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Grid Content */}
                <div className="flex-1 overflow-y-auto p-4 bg-gray-50/50 dark:bg-slate-900/50">
                    {filteredSymbols.length > 0 ? (
                        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
                            {filteredSymbols.map((symbol) => (
                                <button
                                    key={symbol.id || symbol.unicode}
                                    onClick={() => {
                                        recordSymbolUsage(symbolKey(symbol));
                                        onInsert(symbol);
                                        onClose();
                                    }}
                                    className="aspect-square flex flex-col items-center justify-center p-2 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 hover:border-blue-500 dark:hover:border-blue-500 hover:shadow-lg hover:-translate-y-0.5 transition-all group relative"
                                    title={`${symbol.description} (${symbol.trigger.join(', ')})`}
                                >
                                    <span className="text-3xl font-serif mb-1 text-gray-800 dark:text-gray-100">{symbol.unicode}</span>
                                    <span className="text-[10px] text-gray-400 dark:text-gray-500 truncate w-full text-center group-hover:text-blue-600 dark:group-hover:text-blue-400">
                                        {symbol.trigger[0]}
                                    </span>
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-gray-500 gap-2">
                            <Search size={32} className="opacity-20" />
                            <p>No symbols found for "{searchQuery}"</p>
                        </div>
                    )}
                </div>

                {/* Footer Tip */}
                <div className="p-3 text-center text-xs text-gray-400 dark:text-gray-500 border-t dark:border-slate-800 bg-gray-50 dark:bg-slate-950">
                    Tip: You can also strictly type <span className="font-mono bg-gray-200 dark:bg-slate-800 px-1 rounded">Ctrl+Space</span> while writing to find these quickly.
                </div>
            </div>
        </div>
    );
};
